import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminBillingAnomaly, BillingAnomalyResolvedFilter } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useBillingAnomaliesList } from '../api/useAdmin.ts';
import { formatUsdCents } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

const FILTER_TABS: readonly {
  readonly value: BillingAnomalyResolvedFilter;
  readonly labelKey: I18nKey;
  readonly hintKey: I18nKey;
}[] = [
  {
    value: 'unresolved',
    labelKey: 'billing_anomalies.filter.unresolved',
    hintKey: 'billing_anomalies.filter.unresolved.hint',
  },
  {
    value: 'resolved',
    labelKey: 'billing_anomalies.filter.resolved',
    hintKey: 'billing_anomalies.filter.resolved.hint',
  },
  {
    value: 'all',
    labelKey: 'billing_anomalies.filter.all',
    hintKey: 'billing_anomalies.filter.all.hint',
  },
] as const;

const PAGE_LIMIT = 50;

function truncateId(value: string, head = 8, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function formatRelative(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return '—';
  const delta = Date.now() - parsed;
  const seconds = Math.round(delta / 1000);
  if (seconds < 60) return `${Math.max(seconds, 0)}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function BillingAnomalyRow({
  row,
  zebra,
}: {
  readonly row: AdminBillingAnomaly;
  readonly zebra: 'odd' | 'even';
}): JSX.Element {
  const zebraClass = zebra === 'odd' ? 'bg-surface-container' : 'bg-surface-container-low';
  return (
    <tr className={`${zebraClass} transition-colors hover:bg-surface-bright`}>
      <td className="px-4 py-3 font-mono text-[12px]">
        <Link
          to={`/customers/accounts/${encodeURIComponent(row.account_id)}`}
          title={row.account_id}
          className="text-on-surface-variant hover:text-primary"
        >
          {truncateId(row.account_id)}
        </Link>
      </td>
      <td className="px-4 py-3 font-mono text-[12px] text-on-surface-variant" title={row.request_id}>
        {truncateId(row.request_id)}
      </td>
      <td className="px-4 py-3 text-[12px] text-on-surface">
        {row.vendor} / {row.model}
      </td>
      <td className="px-4 py-3 text-right font-mono text-[13px] font-semibold text-on-surface">
        {formatUsdCents(row.amount_usd_cents)}
      </td>
      <td className="px-4 py-3 text-[12px] text-on-surface">{row.reason}</td>
      <td className="px-4 py-3 text-[12px] text-on-surface-variant">
        {row.last_replay_status ?? '—'}
      </td>
      <td className="px-4 py-3 text-[12px] text-on-surface-variant">{formatRelative(row.created_at)}</td>
      <td className="px-4 py-3 text-[12px] text-on-surface-variant">
        {row.resolved_at === null ? '—' : formatRelative(row.resolved_at)}
      </td>
    </tr>
  );
}

export function BillingAnomalies(): JSX.Element {
  const t = useT();
  const [filter, setFilter] = useState<BillingAnomalyResolvedFilter>('unresolved');
  const [offset, setOffset] = useState(0);
  const query = useBillingAnomaliesList(filter, { limit: PAGE_LIMIT, offset });

  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const rows = useMemo(() => query.data?.billing_anomalies ?? [], [query.data]);
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  function handleFilterChange(next: BillingAnomalyResolvedFilter): void {
    setFilter(next);
    setOffset(0);
  }

  return (
    <section data-testid="billing-anomalies-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('billing_anomalies.title')}
        </h2>
        <p className="text-sm text-outline">{t('billing_anomalies.subtitle')}</p>
      </header>

      <nav
        aria-label={t('billing_anomalies.title')}
        className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-container-low p-2"
      >
        {FILTER_TABS.map((tab) => {
          const isActive = tab.value === filter;
          return (
            <button
              key={tab.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => handleFilterChange(tab.value)}
              title={t(tab.hintKey)}
              className={[
                'inline-flex items-center justify-center rounded-lg px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isActive
                  ? 'bg-surface-container-highest text-primary'
                  : 'text-outline hover:bg-surface-container hover:text-on-surface',
              ].join(' ')}
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </nav>

      {query.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{t('billing_anomalies.error.title')}</span>
            <span className="text-xs">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2 rounded-xl bg-surface-container p-4">
          {Array.from({ length: 8 }).map((_, idx) => (
            <div key={idx} className="h-12 animate-pulse rounded-lg bg-surface-container-highest/30" />
          ))}
        </div>
      ) : null}

      {!query.isPending && !query.isError && rows.length === 0 ? (
        <div className="rounded-xl bg-surface-container p-8 text-center">
          <h3 className="text-lg font-semibold text-on-surface">
            {filter === 'unresolved'
              ? t('billing_anomalies.empty.unresolved')
              : filter === 'resolved'
                ? t('billing_anomalies.empty.resolved')
                : t('billing_anomalies.empty.all')}
          </h3>
          <p className="mt-2 text-sm text-outline">{t('billing_anomalies.empty.hint')}</p>
        </div>
      ) : null}

      {!query.isPending && !query.isError && rows.length > 0 ? (
        <div className="overflow-hidden rounded-xl bg-surface-container shadow-[0_12px_40px_rgba(0,0,0,0.16)]">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left">
              <thead className="bg-surface-container-high text-[11px] uppercase tracking-[0.08em] text-outline">
                <tr>
                  <th className="px-4 py-3 font-semibold">{t('billing_anomalies.column.account')}</th>
                  <th className="px-4 py-3 font-semibold">{t('billing_anomalies.column.request_id')}</th>
                  <th className="px-4 py-3 font-semibold">{t('billing_anomalies.column.model')}</th>
                  <th className="px-4 py-3 text-right font-semibold">{t('billing_anomalies.column.amount')}</th>
                  <th className="px-4 py-3 font-semibold">{t('billing_anomalies.column.reason')}</th>
                  <th className="px-4 py-3 font-semibold">{t('billing_anomalies.column.replay')}</th>
                  <th className="px-4 py-3 font-semibold">{t('billing_anomalies.column.created')}</th>
                  <th className="px-4 py-3 font-semibold">{t('billing_anomalies.column.resolved')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <BillingAnomalyRow key={row.id} row={row} zebra={index % 2 === 0 ? 'even' : 'odd'} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-outline-variant/20 px-4 py-3 text-[12px] text-outline">
            <span>{t('billing_anomalies.pagination.summary', { shown: rows.length, total })}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_LIMIT))}
                disabled={!hasPrev}
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('common.previous')}
              </button>
              <button
                type="button"
                onClick={() => setOffset((prev) => prev + PAGE_LIMIT)}
                disabled={!hasNext}
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
